import { useState, useEffect } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import axios from 'axios'
import { initialEventState } from '../utils/eventDefaults'
import { transformEventForEdit } from '../utils/eventDataTransform'
import { buildEventDays, spansMultipleDays } from '../utils/eventDateUtils'
import { describeTicketmasterEvent } from '../utils/ticketmasterUtils'
import { useEventArrays } from './useEventArrays'
import { useEventSubmission } from './useEventSubmission'

import { API_BASE_URL } from '../config/api'

// Transform Ticketmaster/discovered event to local event format
const transformDiscoveredEvent = (discoveredEvent) => {
  const artists = discoveredEvent.artists || []

  return {
    ...initialEventState,
    name: discoveredEvent.name || '',
    startDate: discoveredEvent.date || '',
    endDate: discoveredEvent.endDate || discoveredEvent.date || '',
    startTime: discoveredEvent.time || '',
    endTime: discoveredEvent.endTime || '',
    timeTBA: discoveredEvent.timeTBA || false,
    endTimeTBA: discoveredEvent.endTimeTBA || false,
    dateTBA: discoveredEvent.dateTBA || false,
    noSpecificTime: discoveredEvent.noSpecificTime || false,
    timezone: discoveredEvent.timezone || '',
    // Ticketmaster states this directly; comparing the dates is the fallback
    // for a festival that carries an end date without the flag.
    spanMultipleDays: !!discoveredEvent.spanMultipleDays || spansMultipleDays(
      discoveredEvent.date,
      discoveredEvent.endDate || discoveredEvent.date
    ),
    additionalDates: buildEventDays(discoveredEvent.date, discoveredEvent.endDate),
    venueName: discoveredEvent.venue?.name || '',
    venueAddress: discoveredEvent.venue?.address || '',
    country: discoveredEvent.venue?.country || '',
    // Empty when artists are structured — they show as bubbles, and duplicating
    // them here renders every name again as one space-joined string.
    lineup: '',
    artistsSeen: artists.length > 0 ? artists.map(artist => ({
      name: artist.name,
      genre: artist.genre || '',
      confirmed: true,
      seen: false,
      favorite: false,
      performanceDate: discoveredEvent.date || '',
      setTime: '',
      performance: '',
      comment: ''
    })) : [],
    description: describeTicketmasterEvent(discoveredEvent),
    ticketmasterData: discoveredEvent
  }
}

export const useNewEvent = (mode = 'create') => {
  const { id } = useParams()
  const location = useLocation()
  const isEditMode = mode === 'edit' || (id && id !== 'new')
  const eventId = id

  // Check if there's a pre-selected event from discover widget
  const selectedEvent = location.state?.selectedEvent
  const initialState = selectedEvent ? transformDiscoveredEvent(selectedEvent) : initialEventState

  const [event, setEvent] = useState(initialState)
  const [fetchLoading, setFetchLoading] = useState(false)
  const [fetchError, setFetchError] = useState('')

  const { loading, error, success, createdEvent, submitEvent } = useEventSubmission(isEditMode, eventId)
  const arrayHandlers = useEventArrays(event, setEvent)

  useEffect(() => {
    if (isEditMode && eventId) {
      fetchEventForEdit()
    } else if (selectedEvent) {
      // If there's a selected event, set it (handles navigation from discover widget)
      setEvent(transformDiscoveredEvent(selectedEvent))
    }
  }, [isEditMode, eventId]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEventForEdit = async () => {
    try {
      setFetchLoading(true)
      const response = await axios.get(`${API_BASE_URL}/api/events/${eventId}`, {
        withCredentials: true
      })
      setEvent(transformEventForEdit(response.data))
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching event:', err)
      setFetchError('Failed to load event for editing')
    } finally {
      setFetchLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setEvent(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = (mediaFiles = [], skipDuplicateCheck = false) => {
    return submitEvent(event, mediaFiles, skipDuplicateCheck)
  }

  return {
    event,
    setEvent,
    loading: fetchLoading || loading,
    error: fetchError || error,
    success,
    createdEvent,
    isEditMode,
    eventId,
    handleInputChange,
    ...arrayHandlers,
    submitEvent: handleSubmit
  }
}